import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { BoundingBox } from '@/type/detection.types';
import { useDetectionData } from '@/hooks/load-detection.hooks';
import { twMerge } from 'tailwind-merge';
import Loader from '@/components/Loading/Loader';
import { Switch } from '@/components/Controls/Switch';
import { ResultVideo } from './ResultVideo';
import { TFunction } from 'i18next';
import './styles.css';

interface DetectionVideoPlayerProps {
  t: TFunction<undefined>;
  videoRef: RefObject<HTMLVideoElement>;
  videoUrl?: string;
  isLoadingVideo?: boolean;
  detectionFiles?: {
    damagesUrl?: string;
    detectionsUrl?: string;
  };
  chapterData?: Array<{
    startTime: number;
    endTime: number;
    title: string;
  }>;
  videoContainerClassName?: string;
  toggleTextClassName?: string;
  videoClassName?: string;
  onLoadedMetadata?: () => void;
}

const DEFAULT_ORIGINAL_WIDTH = 250;
const DEFAULT_ORIGINAL_HEIGHT = 420;

const DetectionVideoPlayer = ({
  t,
  videoUrl,
  isLoadingVideo,
  detectionFiles,
  chapterData,
  videoContainerClassName,
  toggleTextClassName,
  videoClassName,
  onLoadedMetadata,
  videoRef,
}: DetectionVideoPlayerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentTimestamp, setCurrentTimestamp] = useState<number>(0);
  const [originalDimensions, setOriginalDimensions] = useState({
    width: DEFAULT_ORIGINAL_WIDTH,
    height: DEFAULT_ORIGINAL_HEIGHT,
  });

  const [showDamage, setShowDamage] = useState(true);
  const [showDetection, setShowDetection] = useState(true);
  const [showChapterMarkers, setShowChapterMarkers] = useState(false);
  const [videoRenderDimensions, setVideoRenderDimensions] = useState({
    width: 0,
    height: 0,
  });

  // Use React Query hooks for data fetching - only load when needed
  const { data: damages = [], isLoading: isLoadingDamages } = useDetectionData(
    showDamage ? detectionFiles?.damagesUrl : undefined,
  );

  const { data: detections = [], isLoading: isLoadingDetections } =
    useDetectionData(showDetection ? detectionFiles?.detectionsUrl : undefined);

  // Update original dimensions when detection data is loaded
  useEffect(() => {
    let nextWidth = DEFAULT_ORIGINAL_WIDTH;
    let nextHeight = DEFAULT_ORIGINAL_HEIGHT;

    if (detections && detections.length > 0) {
      const [width, height] = detections[0].video_size.split('x').map(Number);
      nextWidth = width;
      nextHeight = height;
    }

    setOriginalDimensions((prev) => {
      if (prev.width === nextWidth && prev.height === nextHeight) {
        return prev;
      }
      return { width: nextWidth, height: nextHeight };
    });
  }, [detections]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const updateTimestamp = () => {
      setCurrentTimestamp(video.currentTime);
    };

    video.addEventListener('timeupdate', updateTimestamp);
    video.addEventListener('pause', updateTimestamp);
    video.addEventListener('seeked', updateTimestamp);

    return () => {
      video.removeEventListener('timeupdate', updateTimestamp);
      video.removeEventListener('pause', updateTimestamp);
      video.removeEventListener('seeked', updateTimestamp);
    };
  }, [videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const updateActualVideoDimensions = () => {
      const videoRect = video.getBoundingClientRect();
      // Only update state if the dimensions actually changed
      setVideoRenderDimensions((prev) => {
        if (
          prev.width === videoRect.width &&
          prev.height === videoRect.height
        ) {
          return prev;
        }
        // Also update the canvas size to match exactly
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = videoRect.width;
          canvas.height = videoRect.height;
          canvas.style.width = `${videoRect.width}px`;
          canvas.style.height = `${videoRect.height}px`;
        }
        return { width: videoRect.width, height: videoRect.height };
      });
    };

    // Update on important video events and window resize
    video.addEventListener('loadedmetadata', updateActualVideoDimensions);
    video.addEventListener('loadeddata', updateActualVideoDimensions);
    video.addEventListener('play', updateActualVideoDimensions);
    video.addEventListener('pause', updateActualVideoDimensions);
    window.addEventListener('resize', updateActualVideoDimensions);

    // Call it immediately if the video is already loaded
    if (video.readyState >= 1) {
      updateActualVideoDimensions();
    }

    return () => {
      video.removeEventListener('loadedmetadata', updateActualVideoDimensions);
      video.removeEventListener('loadeddata', updateActualVideoDimensions);
      video.removeEventListener('play', updateActualVideoDimensions);
      video.removeEventListener('pause', updateActualVideoDimensions);
      window.removeEventListener('resize', updateActualVideoDimensions);
    };
  }, [videoRef]);

  const forceRedrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const drawBoxes = (
      boxes: BoundingBox[],
      color: string,
      isDamage: boolean,
    ) => {
      const threshold = 0.01;
      const matchingBoxes = boxes.filter(
        (box) => Math.abs(box.timestamp - currentTimestamp) <= threshold,
      );

      const scaleX = videoRenderDimensions.width / originalDimensions.width;
      const scaleY = videoRenderDimensions.height / originalDimensions.height;

      matchingBoxes.forEach((box) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.fillStyle = color;

        if (isDamage && box.polygon_points && box.polygon_points.length > 0) {
          ctx.beginPath();
          const firstPoint = box.polygon_points[0];
          ctx.moveTo(firstPoint.x * scaleX, firstPoint.y * scaleY);

          for (let i = 1; i < box.polygon_points.length; i++) {
            const point = box.polygon_points[i];
            ctx.lineTo(point.x * scaleX, point.y * scaleY);
          }

          ctx.closePath();
          ctx.stroke();
          ctx.fillStyle = `${color}33`; // Add transparency to the fill
          ctx.fill();

          ctx.font = '12px Arial';
          ctx.fillStyle = color; // Reset fill style for text
          ctx.fillText(
            `${box.className} (${Math.round(box.confidence * 100)}%)`,
            box.polygon_points[0].x * scaleX,
            box.polygon_points[0].y * scaleY - 5,
          );
        } else {
          const scaledX1 = box.x1 * scaleX;
          const scaledY1 = box.y1 * scaleY;
          const scaledX2 = box.x2 * scaleX;
          const scaledY2 = box.y2 * scaleY;

          ctx.beginPath();
          ctx.rect(
            scaledX1,
            scaledY1,
            scaledX2 - scaledX1,
            scaledY2 - scaledY1,
          );
          ctx.stroke();

          ctx.font = '12px Arial';
          ctx.fillText(
            `${box.className} (${Math.round(box.confidence * 100)}%)`,
            scaledX1,
            scaledY1 - 5,
          );
        }
      });
    };

    if (damages.length > 0 && showDamage) {
      drawBoxes(damages, 'red', true);
    }

    if (detections.length > 0 && showDetection) {
      drawBoxes(detections, 'blue', false);
    }
  }, [
    currentTimestamp,
    damages,
    detections,
    originalDimensions,
    showDamage,
    showDetection,
    videoRenderDimensions,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const handlePause = () => {
      // Force redraw when paused
      forceRedrawCanvas();
    };

    const handleSeeked = () => {
      // Force redraw when seeked
      forceRedrawCanvas();
    };

    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);

    return () => {
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [forceRedrawCanvas, videoRef]);

  useEffect(() => {
    forceRedrawCanvas();
  }, [
    currentTimestamp,
    damages,
    detections,
    originalDimensions,
    showDamage,
    showDetection,
    videoRenderDimensions,
    forceRedrawCanvas,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return undefined;

    const resizeCanvas = () => {
      canvas.width = video.clientWidth;
      canvas.height = video.clientHeight;
    };

    video.addEventListener('loadedmetadata', resizeCanvas);

    window.addEventListener('resize', resizeCanvas);

    return () => {
      video.removeEventListener('loadedmetadata', resizeCanvas);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [videoRef]);

  // Function to get chapter title for a given time
  const getChapterTitleForTime = useCallback(
    (time: number) => {
      if (!chapterData || chapterData.length === 0) return '';

      const currentChapter = chapterData.find(
        (chapter) => time >= chapter.startTime && time < chapter.endTime,
      );

      return currentChapter?.title || '';
    },
    [chapterData],
  );

  // Handle mouse move on timeline
  const handleTimelineMouseMove = useCallback(
    (event: MouseEvent) => {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      // Calculate percentage based on entire player width
      const rect = videoElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));

      const duration = videoElement?.duration || 0;
      const hoverTime = percentage * duration;

      const chapterTitle = getChapterTitleForTime(hoverTime);

      setHoverPreview({
        isVisible: true,
        time: hoverTime,
        title: chapterTitle,
      });
    },
    [getChapterTitleForTime, videoRef],
  );

  // Add event listeners for timeline hover - attach to entire player
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return undefined;

    const handlePlayerMouseMove = (event: MouseEvent) => {
      // Check if mouse is over the controls area (bottom part of player)
      const rect = videoElement.getBoundingClientRect();
      const mouseY = event.clientY - rect.top;
      const playerHeight = rect.height;

      // Only show hover preview if mouse is in the bottom 20% of the player (controls area)
      if (mouseY > playerHeight * 0.8) {
        handleTimelineMouseMove(event);
      } else {
        setHoverPreview((prev) => ({ ...prev, isVisible: false }));
      }
    };

    const handlePlayerMouseLeave = () => {
      setHoverPreview((prev) => ({ ...prev, isVisible: false }));
    };

    videoElement.addEventListener('mousemove', handlePlayerMouseMove);
    videoElement.addEventListener('mouseleave', handlePlayerMouseLeave);

    return () => {
      videoElement.removeEventListener('mousemove', handlePlayerMouseMove);
      videoElement.removeEventListener('mouseleave', handlePlayerMouseLeave);
    };
  }, [handleTimelineMouseMove, videoRef]);

  const [hoverPreview, setHoverPreview] = useState<{
    isVisible: boolean;
    time: number;
    title: string;
  }>({
    isVisible: false,
    time: 0,
    title: '',
  });

  return (
    <>
      {videoUrl ? (
        <>
          <div className="flex justify-center">
            <div className="mb-6 flex flex-col justify-center flex-wrap max-w-[370px] gap-3">
              <Switch
                leftText={t('displayDamageDetection')}
                switchClassName={twMerge('', showDamage && '!bg-red-FF2929')}
                leftTextClassName={twMerge('flex-1 mr-2', toggleTextClassName)}
                checked={showDamage}
                onChange={() => setShowDamage((prevState) => !prevState)}
                disabled={isLoadingDamages}
              />
              <Switch
                leftText={t('displayEquipmentDetection')}
                leftTextClassName={twMerge('flex-1 mr-2', toggleTextClassName)}
                checked={showDetection}
                onChange={() => setShowDetection((prevState) => !prevState)}
                disabled={isLoadingDetections}
              />
            </div>
          </div>
          <div
            className={twMerge(
              'relative w-full h-full bg-black rounded-lg flex justify-center items-center overflow-hidden',
              videoContainerClassName,
            )}
          >
            <video
              crossOrigin="anonymous"
              ref={videoRef}
              src={videoUrl}
              controls
              controlsList="nodownload nofullscreen nopictureinpicture"
              disablePictureInPicture
              preload="metadata"
              className={twMerge('h-[50vh]', videoClassName)}
              onLoadedMetadata={() => {
                onLoadedMetadata?.();
                setShowChapterMarkers(true);
              }}
              onPause={() => {
                setShowChapterMarkers(true);
              }}
              onMouseEnter={() => {
                setShowChapterMarkers(true);
              }}
              onMouseLeave={() => {
                if (!videoRef.current?.paused) {
                  setShowChapterMarkers(false);
                }
              }}
            />
            <canvas
              ref={canvasRef}
              className="absolute inset-0 m-auto pointer-events-none"
            />

            {/* Chapter markers container positioned over video timeline */}
            <div
              className={twMerge(
                'm-auto chapter-markers-container transition-opacity duration-300 max-sm:hidden',
                showChapterMarkers ? 'opacity-100' : 'opacity-0',
              )}
              style={{
                width: videoRef.current?.clientWidth || 0,
              }}
            >
              {chapterData?.map((chapter, index) => {
                const videoElement = videoRef.current;
                const duration = videoElement?.duration || 0;
                const percentage =
                  duration > 0 ? (chapter.startTime / duration) * 100 : 0;

                // Only show markers for chapters that start after 0 seconds
                if (chapter.startTime <= 0) return null;

                return (
                  <div
                    key={`chapter-marker-${index}`}
                    className="chapter-marker"
                    style={{
                      left: `${percentage}%`,
                    }}
                    title={`${chapter.title} - ${Math.floor(
                      chapter.startTime / 60,
                    )}:${Math.floor(chapter.startTime % 60)
                      .toString()
                      .padStart(2, '0')}`}
                  />
                );
              })}
            </div>

            {/* Chapter hover preview */}
            {hoverPreview.isVisible && hoverPreview.title && (
              <div className="chapter-preview">
                <div className="chapter-title">{hoverPreview.title}</div>
                <div className="chapter-time">
                  {Math.floor(hoverPreview.time / 60)}:
                  {Math.floor(hoverPreview.time % 60)
                    .toString()
                    .padStart(2, '0')}
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <ResultVideo
          containerClassName="h-[50vh] w-full"
          notFoundText={!isLoadingVideo && t('common:noVideoFound')}
        />
      )}
      {(isLoadingVideo || isLoadingDamages || isLoadingDetections) && (
        <Loader containerClassName="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      )}
    </>
  );
};

export default DetectionVideoPlayer;
